// One row of a doctor↔patient thread: optional day pill, the bubble itself
// (quoted reply, text, attachments) and the meta line underneath.
//
// Shared by DoctorChatInterface and PatientChatInterface so "the same design on
// both sides" is enforced by there being one implementation, not by two files
// being kept in step by hand. It also matches the MOC patient web app's
// components/chat/ChatMessageBubble.jsx bubble-for-bubble.
//
// Declared at MODULE level — an inline component is a new type on every render,
// so React remounts the subtree and the composer below loses focus after one
// keystroke. This codebase has shipped that bug before.

import React from 'react';
import { Download, File as FileIcon, Reply } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ChatAudioPlayer from './ChatAudioPlayer';
import MessageReactionPicker from './MessageReactionPicker';
import MessageStatus from './MessageStatus';
import {
    formatMessageTime,
    isImageAttachment,
    isVoiceAttachment,
    myReaction,
    summariseReactions,
} from './chatShared';
import { getFileURL } from '../../utils/fileURL';

// The server stores replyTo as a plain FK and ships the quoted row alongside it
// as `replyToMessage` (the Sequelize equivalent of mongoose populate). Reading
// `replyTo.message` — as this component's predecessor did — therefore always
// came back undefined for anything that had been through the database, and
// every quoted reply rendered as the word "Attachment".
const quotedOf = (msg) => {
    if (msg?.replyToMessage && typeof msg.replyToMessage === 'object') return msg.replyToMessage;
    if (msg?.replyTo && typeof msg.replyTo === 'object') return msg.replyTo;
    return null;
};

export const quotedTargetId = (msg) => {
    const quoted = quotedOf(msg);
    return quoted?.messageId || quoted?._id || quoted?.id || (msg?.replyTo ?? null);
};

// The chips under a bubble: one per emoji, with its count, the reader's own
// marked in teal. Tapping a chip toggles that emoji — the shortest path to
// "same again" without reopening the picker, and the way every other chat app
// behaves. Module level for the reason given in the header.
const ReactionChips = ({ chips, onToggle, testPrefix, id, isMine }) => (
    <div
        className={`flex flex-wrap gap-1 mt-1 ${isMine ? 'justify-end' : 'justify-start'}`}
        data-testid={`${testPrefix}-reactions-${id}`}
    >
        {chips.map((chip) => (
            <button
                key={chip.emoji}
                type="button"
                onClick={() => onToggle(chip.emoji)}
                className={`flex items-center gap-1 pl-1.5 pr-2 py-0.5 rounded-full border text-[11px] leading-none transition-colors cursor-pointer ${chip.mine
                    ? 'bg-[#4ABDBD]/15 border-[#4ABDBD]/50 text-[#2D8A8A] font-semibold'
                    : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'}`}
                title={chip.emoji}
                aria-pressed={chip.mine}
                data-testid={`${testPrefix}-reaction-chip-${id}-${chip.emoji}`}
            >
                <span className="text-[13px] leading-none">{chip.emoji}</span>
                <span>{chip.count}</span>
            </button>
        ))}
    </div>
);

const ChatMessageBubble = ({
    msg,
    myRole,
    myUserId,
    testPrefix,
    showDateHeader,
    dateLabel,
    onReply,
    onReact,
    onOpenImages,
    onDownload,
    onJumpToQuoted,
}) => {
    const { t } = useTranslation(['chat']);
    const id = msg._id || msg.id;
    const isMine = msg.type === 'user';
    const attachments = Array.isArray(msg.attachments) ? msg.attachments : [];
    const images = attachments.filter(isImageAttachment);
    const others = attachments.filter((f) => !isImageAttachment(f));
    const quoted = quotedOf(msg);
    const status = msg.status === 'read' || msg.isRead ? 'read' : (msg.status || 'sent');

    // The reactor identity the server stamps rows with: the thread-side id and
    // role, the same pair this client sends as senderId/senderRole.
    const me = { userId: myUserId, userRole: myRole };
    const chips = summariseReactions(msg.reactions, me);
    const mineEmoji = myReaction(msg.reactions, me);

    const quotedAuthor = quoted?.senderRole === myRole
        ? t('chat.you', 'You')
        : quoted?.senderRole === 'doctor' ? t('chat.doctor', 'Doctor') : t('chat.patient', 'Patient');

    return (
        <>
            {showDateHeader && dateLabel && (
                <div className="flex justify-center my-3" data-testid={`${testPrefix}-date-header-${id}`}>
                    <span className="bg-white border border-gray-200 text-gray-500 px-3 py-1 rounded-full text-[11px] font-medium shadow-sm" data-testid={`${testPrefix}-date-text-${id}`}>
                        {dateLabel}
                    </span>
                </div>
            )}

            <div
                id={`message-${id}`}
                className={`flex ${isMine ? 'justify-end' : 'justify-start'} mb-2 group/msg`}
                data-testid={`${testPrefix}-message-${id}`}
            >
                <div className={`relative flex flex-col ${isMine ? 'items-end' : 'items-start'} max-w-[75%]`}>
                    <div
                        className={`rounded-2xl px-4 py-2.5 shadow-sm transition-colors ${isMine
                            ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-br-md'
                            : 'bg-white text-gray-800 border border-gray-200 rounded-bl-md'} ${msg.pending ? 'opacity-70' : ''}`}
                        data-testid={`${testPrefix}-message-bubble-${id}`}
                    >
                        {quoted && (
                            <button
                                type="button"
                                onClick={() => onJumpToQuoted?.(quotedTargetId(msg))}
                                className={`w-full text-left mb-2 p-2 rounded-xl text-xs border-l-4 cursor-pointer hover:opacity-80 transition-opacity ${isMine
                                    ? 'bg-white/10 border-white/40 text-white/90'
                                    : 'bg-gray-50 border-[#4ABDBD] text-gray-600'}`}
                                data-testid={`${testPrefix}-reply-quote-${id}`}
                            >
                                <span className="font-semibold text-[10px] mb-0.5 opacity-80 flex items-center gap-1">
                                    <Reply size={10} />
                                    {quotedAuthor}
                                </span>
                                <span className="block line-clamp-2">
                                    {quoted.message || quoted.content || t('chat.attachment', 'Attachment')}
                                </span>
                            </button>
                        )}

                        {msg.content && (
                            <p className="text-sm whitespace-pre-wrap break-words leading-relaxed" data-testid={`${testPrefix}-message-content-${id}`}>
                                {msg.content}
                            </p>
                        )}

                        {attachments.length > 0 && (
                            <div className={`grid gap-2 min-w-[200px] ${msg.content ? 'mt-2' : ''}`} data-testid={`${testPrefix}-attachments-${id}`}>
                                {images.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => onOpenImages(images)}
                                        className={`relative rounded-xl overflow-hidden border cursor-pointer ${isMine ? 'border-white/20 bg-white/10' : 'border-gray-100 bg-gray-50'}`}
                                        data-testid={`${testPrefix}-image-gallery-${id}`}
                                    >
                                        <img
                                            src={getFileURL(images[0].url)}
                                            alt={images[0].fileName || t('chat.image_alt', 'Image')}
                                            className="max-w-full h-auto max-h-52 object-contain mx-auto"
                                            data-testid={`${testPrefix}-image-${id}`}
                                        />
                                        {images.length > 1 && (
                                            <span className="absolute inset-0 bg-black/60 flex items-center justify-center text-white text-3xl font-bold" data-testid={`${testPrefix}-image-count-${id}`}>
                                                +{images.length - 1}
                                            </span>
                                        )}
                                    </button>
                                )}

                                {others.map((file, i) => (
                                    <div
                                        key={`${id}-file-${i}`}
                                        className={`rounded-xl overflow-hidden border ${isMine ? 'border-white/20 bg-white/10' : 'border-gray-100 bg-gray-50'}`}
                                        data-testid={`${testPrefix}-file-attachment-${id}-${i}`}
                                    >
                                        {isVoiceAttachment(file) ? (
                                            <ChatAudioPlayer src={getFileURL(file.url)} isUser={isMine} />
                                        ) : (
                                            <div className="p-3 flex items-center gap-3">
                                                <span className={`p-2 rounded-lg ${isMine ? 'bg-white/20' : 'bg-white shadow-sm'}`}>
                                                    <FileIcon size={18} />
                                                </span>
                                                <span className="flex-1 min-w-0">
                                                    <span className="block text-xs font-bold truncate" data-testid={`${testPrefix}-file-name-${id}-${i}`}>
                                                        {file.fileName || t('chat.attachment', 'Attachment')}
                                                    </span>
                                                    <span className="block text-[10px] opacity-60 uppercase">
                                                        {file.mimeType ? file.mimeType.split('/')[1] : t('chat.file', 'File')}
                                                    </span>
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => onDownload(file.url, file.fileName)}
                                                    className={`p-2 rounded-lg transition-colors cursor-pointer ${isMine ? 'hover:bg-white/20' : 'hover:bg-gray-200'}`}
                                                    title={t('chat.download', 'Download')}
                                                    aria-label={t('chat.download', 'Download')}
                                                    data-testid={`${testPrefix}-file-download-button-${id}-${i}`}
                                                >
                                                    <Download size={17} />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {chips.length > 0 && (
                        <ReactionChips
                            chips={chips}
                            onToggle={(emoji) => onReact?.(msg, emoji)}
                            testPrefix={testPrefix}
                            id={id}
                            isMine={isMine}
                        />
                    )}

                    <span className="flex items-center text-[10px] text-gray-400 mt-0.5 px-1" data-testid={`${testPrefix}-message-timestamp-${id}`}>
                        {msg.pending ? t('chat.sending', 'Sending…') : formatMessageTime(msg.timestamp)}
                        {isMine && <MessageStatus status={status} />}
                    </span>

                    {/* Reply and react are offered on hover only — permanent
                        buttons on every row turn a calm thread into a wall of
                        controls.

                        WITH ONE EXCEPTION: a touch screen has no hover, so on a
                        coarse pointer the cluster is simply always visible.
                        Without that branch the reply and reaction affordances
                        exist only for people with a mouse, which on the patient
                        side is almost nobody.

                        Not offered on an in-flight message: it carries a
                        `pending-*` id, not a messages.id, so neither the reply
                        link nor a reaction could be stored against it. */}
                    {!msg.pending && (
                        <div
                            className={`absolute top-2 flex items-center gap-1 opacity-0 group-hover/msg:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity z-20 ${isMine ? 'right-[calc(100%+8px)]' : 'left-[calc(100%+8px)]'}`}
                            data-testid={`${testPrefix}-message-actions-${id}`}
                        >
                            {/* The cluster sits OUTSIDE the bubble, so the popover
                                has to open back across the thread rather than off
                                the edge of it: my messages hang on the right of
                                the screen, theirs on the left. */}
                            <MessageReactionPicker
                                onSelect={(emoji) => onReact?.(msg, emoji)}
                                selected={mineEmoji}
                                align={isMine ? 'right' : 'left'}
                                testId={`${testPrefix}-reaction-picker-${id}`}
                            />
                            <button
                                type="button"
                                onClick={() => onReply(msg)}
                                className="p-1.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-500 rounded-full shadow-sm transition-all cursor-pointer"
                                title={t('chat.reply', 'Reply')}
                                aria-label={t('chat.reply', 'Reply')}
                                data-testid={`${testPrefix}-reply-button-${id}`}
                            >
                                <Reply size={13} />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default ChatMessageBubble;
